import type { RemoteConfig } from "@repo/core-utils";
import { useContext } from "react";
import { createContext } from "react";

import { type TelemetryClient, TelemetryUtils } from "./telemetryClient";
import { RemoteConfigReceived } from "./web.schema";

export abstract class BaseTrackingAdapter {
  protected telemetryClient: TelemetryClient;
  protected telemetryUtils: TelemetryUtils;

  constructor(telemetryClient: TelemetryClient) {
    this.telemetryClient = telemetryClient;
    this.telemetryUtils = new TelemetryUtils(this.telemetryClient);
    this.telemetryUtils.trackMachineTranslation();
    this.telemetryUtils.trackSession();
  }

  //eslint-disable-next-line
  protected createTelemetryTrackingFunction<T extends new (...args: any) => any>(
    event: T
  ): (...args: ConstructorParameters<T>) => void {
    //eslint-disable-next-line
    //@ts-ignore TODO: We need to update storybook to handle this
    return (...args) => this.telemetryClient.trackTelemetryEvent(new event(...args));
  }

  getTrackingID(): string {
    return this.telemetryClient.getTrackingID();
  }

  setExperiments(experiments: RemoteConfig) {
    this.telemetryClient.setExperiments(experiments);
  }

  setUserData({ userID }: { userID: string | null }) {
    this.telemetryClient.setUserId(userID);
  }

  reset() {
    this.setUserData({ userID: null });
    this.telemetryClient.resetTrackingID();
  }

  trackRemoteConfigReceived = this.createTelemetryTrackingFunction(RemoteConfigReceived);
}

export const TrackingContext = createContext<BaseTrackingAdapter | undefined>(undefined);

export const useBaseTracker = () => {
  const context = useContext(TrackingContext);

  if (!context) {
    throw new Error("useTracker must be used within a TrackingContextProvider");
  }

  return context;
};
